import React from 'react';
import { Card, Hyperlink, Icon, ActionRow, Button } from '@openedx/paragon';
import { camelCaseObject, getConfig } from '@edx/frontend-platform';
import { ErrorPage, AppContext } from '@edx/frontend-platform/react';
import { getAuthService, getAuthenticatedHttpClient, getAuthenticatedUser, fetchAuthenticatedUser, ensureAuthenticatedUser, hydrateAuthenticatedUser } from '@edx/frontend-platform/auth';

import queryString from 'query-string';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {// frontend-plugin-bulk-rerun exports a component for this authoring MFE slot;
// it is intentionally initialized here instead of registered as a separate MFE.
const { BulkRerunsTab } = await import(
  './plugins/bulk-rerun/src/index.tsx'
);

config.pluginSlots['org.cucwd.frontend.authoring.studio_home_bulk_reruns.v1'] = {
  keepDefault: false,
  plugins: [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'bulk_reruns_tab',
        type: DIRECT_PLUGIN,
        priority: 50,
        RenderWidget: BulkRerunsTab,
      },
    },
  ],
};
const {
  UnitEstimatedTimeEditor,
} = await import('@openedx/estimated-time-to-complete');
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'estimated-time-unit-sidebar-editor-alias',
                priority: 60,
                type: DIRECT_PLUGIN,
                RenderWidget: UnitEstimatedTimeEditor,
              },
            }]);
    }
    if (process.env.APP_ID == 'account') {
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {// Tutor concatenates runtime definitions from every enabled learner-dashboard
// plugin into one env.config scope. Use a plugin-specific alias so this import
// does not redeclare `reduxHooks` when another plugin imports the same hooks.
const { reduxHooks: caSurveyReduxHooks } = await import('hooks');

/*
  Locate the ACCESS_TOKEN_COOKIE_NAME JWT value for the anonymous user.
  This is used to get the sub value for the anonymous user.
  If the user is authenticated, it will return the sub value from the JWT token.
  If the user is not authenticated, it will return null.
  If there is an error, it will return an error object.
*/
function useJwtSub({ requireAuth = false } = {}) {
  const [state, setState] = React.useState({ loading: true, sub: null, error: null });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (requireAuth) {
          // Redirects to login if not authenticated
          await ensureAuthenticatedUser();
        } else {
          await fetchAuthenticatedUser(); // hydrates; returns null if not logged in
        }

        const jwtAuthService = await getAuthService();
        const jwtTokenService = jwtAuthService.getJwtTokenService();
        if (!jwtTokenService) {
          throw new Error('JWT Token Service is not available');
        }

        console.log('jwtTokenService.decodeJwtCookie()', jwtTokenService.decodeJwtCookie());
        const sub = jwtTokenService.decodeJwtCookie()?.sub ?? null;
        if (!cancelled) setState({ loading: false, sub, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, sub: null, error: e });
      }
    })();
    return () => { cancelled = true; };
  }, [requireAuth]);

  return state;
}

/**
 * Get's organizations data. Returns list of organization names.
 */
function normalizeOrganizationDetail(data) {
  return {
    id: data.id,
    ...camelCaseObject(data),
  };
}

function useOrganization(shortCode) {
  const [state, setState] = React.useState(() => ({
    data: null,
    error: null,
    loading: !!shortCode,   // initial loading if we have a code
  }));

  React.useEffect(() => {
    // Always call hooks; handle invalid inputs here instead of early return
    if (!shortCode || typeof shortCode !== 'string') {
      setState({ data: null, error: null, loading: false });
      return;
    }

    let cancelled = false;

    (async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        const url = `${getConfig().LMS_BASE_URL}/api/organizations/v0/organizations/${encodeURIComponent(shortCode)}/`; // trailing slash matters
        const resp = await getAuthenticatedHttpClient().get(url);
        if (cancelled) return;
        const normalized = normalizeOrganizationDetail ? normalizeOrganizationDetail(resp.data) : resp.data;
        setState({ data: normalized, error: null, loading: false });
      } catch (e) {
        if (cancelled) return;
        setState({ data: null, error: e, loading: false });
      }
    })();

    return () => { cancelled = true; };
  }, [shortCode]);

  return state;
}

let didHydrate = false;
let inFlight = null;

/** Run hydrateAuthenticatedUser() at most once per page load */
function useHydrateAuthenticatedUserOnce() {
  React.useEffect(() => {
    if (didHydrate) return;

    if (!inFlight) {
      inFlight = hydrateAuthenticatedUser()
        .catch(() => {})                 // optionally log
        .finally(() => { didHydrate = true; inFlight = null; });
    }
    // If already in flight, just let it finish.
  }, []);
}

const CaSurveyWidget = () => {
  // Grab additional profile information about the user (once-per-page)
  useHydrateAuthenticatedUserOnce();   
  const { authenticatedUser } = React.useContext(AppContext);
  console.log("authenticatedUser", authenticatedUser);
  // const userKeys = Object.keys(authenticatedUser);
  // console.log(userKeys);

  // Fetch the JWT sub for the anonymous user
  let { loading: subLoading, sub, error: subError } = useJwtSub({ requireAuth: true });
  let platform_anonymous_user_id_string = 'platform_anonymous_user_id=';
  if (subError) {
    console.error('Error fetching JWT sub to find anonymous user ID:', subError);
  } else if (!subLoading && !subError) {
    platform_anonymous_user_id_string += sub? sub : '';
  }

  // Find the latest enrolled course
  const filters = caSurveyReduxHooks.useFilters();
  const removeFilter = caSurveyReduxHooks.useRemoveFilter();
  const pageNumber = caSurveyReduxHooks.usePageNumber();
  const setPageNumber = caSurveyReduxHooks.useSetPageNumber();

  const sortBy = 'enrolled';
  const ListPageSize = '25';

  const querySearch = queryString.parse(window.location.search, { parseNumbers: true });

  const { numPages, visibleList } = caSurveyReduxHooks.useCurrentCourseList({
    sortBy,
    filters,
    pageSize: querySearch?.disable_pagination === 1 ? 0 : ListPageSize,
  });

  // useCurrentCourseList returns the complete course-card data, including the
  // provider. Read the first provider directly instead of calling a React hook
  // inside a loop whose length changes with the visible course list.
  const shortCode = visibleList?.[0]?.courseProvider?.name || '';

  // Define the Qualtrics survey parameters
  const username_string = "platform_username=" + authenticatedUser?.username;
  const user_email_string = "platform_email=" + authenticatedUser?.email;
  const platform_fullname = "platform_fullname=" + authenticatedUser?.name;
  
  // Fetch the organization data based on short name
  const { data: orgData, error: orgError, loading: orgLoading } = useOrganization(shortCode);

  let org_institution_name_string = "org_institution_name=";
  const org_institution_shortname_string = "org_institution_short_name=" + shortCode;
  let org_institution_city_string = "org_institution_city=";
  let org_institution_state_string = "org_institution_state=";
  let org_institution_zipcode_string = "org_institution_zipcode=";
  if (!orgLoading && !orgError) {
    org_institution_name_string += orgData?.name || '';
    org_institution_city_string += orgData?.city || '';
    org_institution_state_string += orgData?.state || '';
    org_institution_zipcode_string += orgData?.zipcode || '';
  }

  const zipcode = React.useMemo(() => {
    const ep = authenticatedUser?.extendedProfile ?? [];
    return ep.find(i => i.fieldName === 'zipcode')?.fieldValue ?? '';
  }, [authenticatedUser?.extendedProfile]);
  const demographic_zipcode_string = "demographic_zipcode=" + encodeURIComponent(zipcode);

  const demographic_country_string = "demographic_country=" + (authenticatedUser?.country || '');

  console.log('display_meta', process.env.NODE_ENV);
  const display_meta_string = "display_meta=" + (process.env.NODE_ENV === 'development' ? 1 : 0);

  const preSurveyLink = `https://clemson.qualtrics.com/jfe/form/SV_3C2RY5TuB0uIZ70?${username_string}&${platform_fullname}&${user_email_string}&${platform_anonymous_user_id_string}&${org_institution_name_string}&${org_institution_shortname_string}&${org_institution_city_string}&${org_institution_state_string}&${org_institution_zipcode_string}&${demographic_zipcode_string}&${demographic_country_string}&program_name=Aviation Mechanic General&${display_meta_string}`;

  const postSurveyLink = `https://clemson.qualtrics.com/jfe/form/SV_1Al5SpJgWVv6bFI?${username_string}&${platform_fullname}&${user_email_string}&${platform_anonymous_user_id_string}&${org_institution_name_string}&${org_institution_shortname_string}&${org_institution_city_string}&${org_institution_state_string}&${org_institution_zipcode_string}&${demographic_zipcode_string}&${demographic_country_string}&program_name=Aviation Mechanic General&${display_meta_string}`;

  return (
    <>

    <Card id="ca-pre-survey-widget">
      <Card.Section 
        title="Aviation Mechanic General"
        actions={
          <ActionRow>
            <Button
               size="sm"
               as="a"
               href={preSurveyLink}
               target="_blank"
            >Take Survey</Button>
          </ActionRow>
        }
      >
        <h3>Pre-Program Survey</h3>
        This will help us understand your career awareness and interest, gauge where you are with FAA General maintenance knowledge and skill confidence, career plans, and demographic information.

      </Card.Section>
      <Card.Status
        variant="primary"
      >
        <b>New Learner: Take this survey upon program entry</b>
      </Card.Status>
    </Card>
    <br />
    <Card id="ca-post-survey-widget">
      <Card.Section 
        title="Aviation Mechanic General"
        actions={
          <ActionRow>
            <Button
               size="sm"
               as="a"
               href={postSurveyLink}
               target="_blank"
            >Take Survey</Button>
          </ActionRow>
        }
      >
        <h3>Post-Program Survey</h3>
        This will help us understand your completion of FAA General maintenance program, career awareness and interest, knowledge and skill confidence, and demographic information.

      </Card.Section>
      <Card.Status
        variant="primary"
      >
        <b>Continuing Learner: Take this survey upon program exit</b>
      </Card.Status>
    </Card>
  </>
  );
};
// Tutor concatenates runtime definitions from every enabled learner-dashboard
// plugin into one env.config scope. Use a plugin-specific alias so this import
// does not redeclare `reduxHooks` when another plugin imports the same hooks.
const { reduxHooks: courseCardReduxHooks } = await import('hooks');

const CourseRunMetadata = ({ cardId }) => {
  const courseRun = courseCardReduxHooks.useCardCourseRunData(cardId);

  if (!courseRun?.courseId) {
    return null;
  }

  const segments = courseRun.courseId.split('+');
  const courseRunName = segments.length >= 3
    ? segments[segments.length - 1]
    : null;

  return ` • ${courseRunName}`;
};
      addPlugins(config, 'widget_sidebar_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    }]);
      addPlugins(config, 'widget_sidebar_slot', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'custom_sidebar_panel',
            type: DIRECT_PLUGIN,
            priority: 60,
            RenderWidget: CaSurveyWidget,
        },
    }
    ]);
      addPlugins(config, 'org.openedx.frontend.learner_dashboard.course_card_metadata.v1', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'courserun_metadata',
            type: DIRECT_PLUGIN,
            priority: 60,
            RenderWidget: CourseRunMetadata,
        },
    }
    ]);
    }
    if (process.env.APP_ID == 'learning') {
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
    }
    if (process.env.APP_ID == 'learner-record') {
    }
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;